import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Dimmer, Loader, Message, Table,
} from 'semantic-ui-react';

import '../assets/theme.css';

import GuaranteePurchasesListV2 from '../views/GuaranteePurchases/ListV2';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import GuaranteePurchaseEdit from '../views/GuaranteePurchases/EditForm';
import GuaranteePurchaseExtend from '../views/GuaranteePurchases/ExtendForm';
import GuaranteePurchaseCancel from '../views/GuaranteePurchases/CancelForm';
import GuaranteePurchaseExpireConfirmation from '../views/GuaranteePurchases/ExpireConfirmationModal';
import AuditModal from '../components/AuditModal';
import client from '../apollo';
import useExternalPurchasesList from '../hooks/useExternalPurchasesList';
import externalGuaranteePurchasesQuery from '../graphql/externalGuaranteePurchasesQuery';
import guaranteePurchasesQuery from '../graphql/guaranteePurchasesQuery';
import guaranteePurchaseAuditsQuery from '../graphql/guaranteePurchaseAuditsQuery';
import updateGuaranteePurchaseMutation from '../graphql/updateGuaranteePurchaseMutation';
import cancelGuaranteePurchaseMutation from '../graphql/cancelGuaranteePurchaseMutation';
import expireGuaranteePurchaseMutation from '../graphql/expireGuaranteePurchaseMutation';
import unexpireGuaranteePurchaseMutation from '../graphql/unexpireGuaranteePurchaseMutation';
import APP_PATH from '../constants/paths';
import { PRODUCT_FAMILY_ALL } from '../constants/productFamily';
import { DEFAULT_PAGE_SIZE } from '../constants';

const ACTION_UNAVAILABLE = 'This action is unavailable for this purchase.';

const MODAL_BY_ACTION = {
  Edit: 'edit',
  'Extend Guarantee': 'extend',
  'Cancel Guarantee': 'cancel',
};

const buildVariables = ({
  page, categoryCode, vin, dealer,
}) => ({
  page,
  limit: DEFAULT_PAGE_SIZE,
  categoryCode,
  vin,
  dealer,
});

const renderAuditRow = auditRecord => {
  return (
    <>
      {auditRecord.event === 'update'
        && `${auditRecord.versionAuthor} updated the Guarantee Purchase - ${
          auditRecord.itemType
        } details`}
      {auditRecord.event === 'create'
        && `${auditRecord.versionAuthor} created the Guarantee Purchase - ${
          auditRecord.itemType
        }`}
      {auditRecord.event === 'destroy'
        && `${auditRecord.versionAuthor} Destroyed Guarantee Purchase - ${
          auditRecord.itemType
        }`}
    </>
  );
};

const renderChangesetRow = (auditRecord, change, key) => {
  if (key !== 'updated_at'
    && auditRecord.event === 'update'
    && change[1] !== null
    && change[1] !== '') {
    return (
      <Table.Row key={key}>
        <Table.Cell>
          <span>
            set{' '}
            <span style={{ fontWeight: 'bold' }}>
              {key}{' '}
            </span>
            from{' '}
            <span style={{ fontWeight: 'bold' }}>
              {' '}
              {change[0] || 'nil'}{' '}
            </span>
            to{' '}
            <span style={{ fontWeight: 'bold' }}>
              {change[1]}{' '}
            </span>
          </span>
        </Table.Cell>
      </Table.Row>
    );
  }
  return null;
};

// Tappy builds its guarantee from the APMS event a few seconds to about a minute after the order shows up
// in the APMS list. While rows are still waiting, re-read the list in the background instead of making the
// user reload. Gives up after SYNC_POLL_MAX_IDLE_POLLS polls in a row without any row syncing.
const SYNC_POLL_INTERVAL_MS = 5000;
const SYNC_POLL_MAX_IDLE_POLLS = 24;

const GuaranteePurchasesV2 = ({
  searchFields, history, updateMutation, cancelMutation, expireMutation, unexpireMutation,
}) => {
  const {
    items,
    error,
    refetch,
    refreshSilently,
    start,
    end,
    isLastPage,
    isInitialLoading,
    isPaginationLoading,
    nextHandler,
    previousHandler,
    onSearchSubmit,
  } = useExternalPurchasesList({
    query: externalGuaranteePurchasesQuery,
    responseField: 'externalGuaranteePurchases',
    buildVariables,
  });

  const [openModal, setOpenModal] = useState(null);
  const [legacyPurchase, setLegacyPurchase] = useState(null);
  const [auditPurchaseId, setAuditPurchaseId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [expireMode, setExpireMode] = useState('expire');
  const [expireTarget, setExpireTarget] = useState(null);

  const awaitingKey = items
    .filter(item => !item.guaranteePurchaseId)
    .map(item => item.externalId)
    .join(',');
  const idlePolls = useRef({ key: '', count: 0 });
  // Bumped when a background read ends, which arms the next one - so reads never overlap and the loop
  // does not depend on the response being a new object.
  const [pollRound, setPollRound] = useState(0);

  useEffect(() => {
    if (!awaitingKey) {
      idlePolls.current = { key: '', count: 0 };
      return undefined;
    }
    // A different set of waiting rows means something synced (or a new page/search): start counting again.
    if (idlePolls.current.key !== awaitingKey) {
      idlePolls.current = { key: awaitingKey, count: 0 };
    }
    if (idlePolls.current.count >= SYNC_POLL_MAX_IDLE_POLLS) return undefined;

    const timer = setTimeout(() => {
      // A hidden tab skips the read (and does not count against the budget) but keeps the loop alive.
      if (document.hidden) {
        setPollRound(round => round + 1);
        return;
      }
      idlePolls.current.count += 1;
      Promise.resolve(refreshSilently()).finally(() => setPollRound(round => round + 1));
    }, SYNC_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [awaitingKey, pollRound, refreshSilently]);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(null);
    setLegacyPurchase(null);
    setAuditPurchaseId(null);
    setExpireTarget(null);
    setFormError(null);
    setFormSubmitting(false);
  }, []);

  const fetchLegacyPurchase = useCallback(async row => {
    const { vin, externalId, guaranteePurchaseId } = row;
    if (!vin || !guaranteePurchaseId) return null;

    const result = await client.query({
      query: guaranteePurchasesQuery,
      variables: { vin },
      fetchPolicy: 'network-only',
    });
    const purchases = (result.data && result.data.guaranteePurchases) || [];

    // The local purchase id is the key tappy resolved for this row. Tappy offers that predate their
    // APMS external_id carry externalId: null, so the external id can only be a secondary match.
    return (
      purchases.find(purchase => String(purchase.id) === String(guaranteePurchaseId))
      || purchases.find(
        purchase => externalId
          && purchase.guaranteeOffer
          && purchase.guaranteeOffer.externalId === externalId
      )
      || null
    );
  }, []);

  const onSelectGuarantee = useCallback(
    async(action, row) => {
      setActionError(null);

      if (action === 'Audit') {
        if (!row.guaranteePurchaseId) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        setAuditPurchaseId(row.guaranteePurchaseId);
        setOpenModal('audit');
        return;
      }

      if (action === 'Vehicle Status Tracker') {
        if (!row.guaranteePurchaseId) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        history.push(
          APP_PATH.VEHICLE_STATUS_TRACKER.replace(
            ':purchaseId',
            row.guaranteePurchaseId
          )
        );
        return;
      }

      if (action === 'Expire Guarantee' || action === 'Unexpire Guarantee') {
        if (!row.guaranteePurchaseId) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        setExpireMode(action === 'Expire Guarantee' ? 'expire' : 'unexpire');
        setExpireTarget(row);
        setFormError(null);
        setFormSubmitting(false);
        setOpenModal('expire');
        return;
      }

      const modal = MODAL_BY_ACTION[action];
      if (!modal) return;

      if (!row.guaranteePurchaseId) {
        setActionError(ACTION_UNAVAILABLE);
        return;
      }

      try {
        const record = await fetchLegacyPurchase(row);
        if (!record) {
          setActionError(ACTION_UNAVAILABLE);
          return;
        }
        setFormError(null);
        setFormSubmitting(false);
        setLegacyPurchase(record);
        setOpenModal(modal);
      } catch (fetchError) {
        setActionError(ACTION_UNAVAILABLE);
      }
    },
    [fetchLegacyPurchase, history]
  );

  const submitUpdate = useCallback(
    async(values, successMessage, errorMessage) => {
      setFormSubmitting(true);
      try {
        const { data } = await updateMutation({
          variables: { purchaseInput: { guaranteePurchase: { ...values } } },
        });
        const { success, errors } = data.updateGuaranteePurchase;
        if (!success) {
          setFormSubmitting(false);
          setFormError(errors);
          return;
        }
        setFlashMessage(successMessage);
        closeModal();
        refetch();
      } catch (mutationError) {
        setFormSubmitting(false);
        setFormError(errorMessage);
      }
    },
    [closeModal, refetch, updateMutation]
  );

  const onEditFormSubmitHandler = useCallback(
    values => submitUpdate(
      values,
      'Successfully Updated Guarantee Purchase',
      'Error while updating Guarantee Purchase'
    ),
    [submitUpdate]
  );

  const onExtendFormSubmitHandler = useCallback(
    values => submitUpdate(
      values,
      'Successfully extended Guarantee Purchase',
      'Error while extending Guarantee Purchase'
    ),
    [submitUpdate]
  );

  const onCancelFormSubmitHandler = useCallback(
    async values => {
      setFormSubmitting(true);
      try {
        const { data } = await cancelMutation({
          variables: { cancelInput: { ...values, productFamily: PRODUCT_FAMILY_ALL } },
        });
        const { success, errors } = data.cancelGuaranteePurchase;
        if (!success) {
          setFormSubmitting(false);
          setFormError(errors);
          return;
        }
        setFlashMessage('Successfully Cancelled Guarantee Purchase');
        closeModal();
        refetch();
      } catch (mutationError) {
        setFormSubmitting(false);
        setFormError('Error while cancelling Guarantee Purchase');
      }
    },
    [closeModal, refetch, cancelMutation]
  );

  const onExpireConfirmHandler = useCallback(async() => {
    setFormSubmitting(true);
    const isExpire = expireMode === 'expire';
    try {
      const mutation = isExpire ? expireMutation : unexpireMutation;
      const mutationName = isExpire ? 'expireGuaranteePurchase' : 'unexpireGuaranteePurchase';
      const inputName = isExpire ? 'expireInput' : 'unexpireInput';
      const { data } = await mutation({
        variables: { [inputName]: { guaranteePurchaseId: expireTarget.guaranteePurchaseId } },
      });
      const { success, errors } = data[mutationName];
      if (!success) {
        setFormSubmitting(false);
        setFormError(errors.join(', '));
        return;
      }
      setFlashMessage(
        isExpire
          ? 'Successfully Expired Guarantee Purchase'
          : 'Successfully Unexpired Guarantee Purchase'
      );
      closeModal();
      refetch();
    } catch (mutationError) {
      setFormSubmitting(false);
      setFormError(`Error while ${isExpire ? 'expiring' : 'unexpiring'} Guarantee Purchase`);
    }
  }, [closeModal, refetch, expireMutation, unexpireMutation, expireMode, expireTarget]);

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      {actionError && <Message negative content={actionError} />}
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmit}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumbers']}
        multipleSearches
      />
      <Dimmer.Dimmable dimmed={isInitialLoading}>
        <Dimmer active={isInitialLoading} inverted>
          <Loader data-testid='loading-overlay'>Loading...</Loader>
        </Dimmer>

        <GuaranteePurchasesListV2
          onSelectGuarantee={onSelectGuarantee}
          items={items}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={nextHandler}
          previousHandler={previousHandler}
          loading={isPaginationLoading}
          lastPage={isLastPage}
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </Dimmer.Dimmable>

      {legacyPurchase && openModal === 'edit' && (
        <GuaranteePurchaseEdit
          isModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onEditFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {legacyPurchase && openModal === 'extend' && (
        <GuaranteePurchaseExtend
          isModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onExtendFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {legacyPurchase && openModal === 'cancel' && (
        <GuaranteePurchaseCancel
          isCancelModalOpen
          onCloseHandler={closeModal}
          onSubmitHandler={onCancelFormSubmitHandler}
          guaranteePurchase={legacyPurchase}
          error={formError}
          submitting={formSubmitting}
        />
      )}

      {expireTarget && openModal === 'expire' && (
        <GuaranteePurchaseExpireConfirmation
          open
          mode={expireMode}
          vin={expireTarget.vin || ''}
          loading={formSubmitting}
          errorMessage={formError}
          onClose={closeModal}
          onConfirm={onExpireConfirmHandler}
        />
      )}

      {auditPurchaseId && openModal === 'audit' && (
        <AuditModal
          header='Status History - Guarantee Purchases'
          fetchAuditRecordQuery={guaranteePurchaseAuditsQuery}
          queryVariables={{ guaranteePurchaseId: parseInt(auditPurchaseId, 10) }}
          auditResponseAttribute='guaranteePurchaseAudits'
          renderAuditRow={renderAuditRow}
          renderChangesetRow={renderChangesetRow}
          isAuditModalOpen
          onCloseHandler={closeModal}
        />
      )}
    </div>
  );
};

GuaranteePurchasesV2.propTypes = {
  searchFields: PropTypes.shape({
    vin: PropTypes.string,
    dealerNumbers: PropTypes.array,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  updateMutation: PropTypes.func,
  cancelMutation: PropTypes.func,
  expireMutation: PropTypes.func,
  unexpireMutation: PropTypes.func,
};

GuaranteePurchasesV2.defaultProps = {
  updateMutation: null,
  cancelMutation: null,
  expireMutation: null,
  unexpireMutation: null,
};

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return { searchFields };
};

export default compose(
  withRouter,
  connect(mapStateToProps),
  graphql(updateGuaranteePurchaseMutation, { name: 'updateMutation' }),
  graphql(cancelGuaranteePurchaseMutation, { name: 'cancelMutation' }),
  graphql(expireGuaranteePurchaseMutation, { name: 'expireMutation' }),
  graphql(unexpireGuaranteePurchaseMutation, { name: 'unexpireMutation' })
)(GuaranteePurchasesV2);
